import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import styled from 'styled-components'
import { ClearButton } from '../ClearButton'
import {
  ClearButtonAndChildrenWrapper,
  FormContent,
  Form,
  LoupeIcon,
  LoupeIconContainer,
  SearchInput,
  Wrapper,
} from './Filter.styles'

export interface FilterProps {
  className?: string
  name?: string
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void
  onChange?: (value: string) => void
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void
  onSubmit?: (event?: React.FormEvent<HTMLFormElement>) => void
  onClear?: () => void
  placeholder?: string
  value?: string
  width?: number
  isCWV?: boolean
  disabled?: boolean
  children?: React.ReactNode
  fullWidth?: boolean
  collapsible?: boolean
  onExpandedChange?: (expanded: boolean) => void
}

const FilterBase = function FilterBase({
  ref,
  ...props
}: FilterProps & {
  ref?: React.RefObject<HTMLInputElement | null>
}) {
  const {
    className,
    name,
    onBlur = () => {},
    onChange = () => {},
    onFocus = () => {},
    onKeyDown = () => {},
    onSubmit = () => {},
    onClear = () => {},
    placeholder = 'Search',
    value = '',
    width,
    children,
    isCWV,
    disabled,
    fullWidth,
    collapsible,
    onExpandedChange,
    ...rest
  }: typeof props = props

  const inputRef = useRef<HTMLInputElement>(null)
  useImperativeHandle(ref, () => inputRef.current!)

  // This will allow us to control the keyup handler on a global level. For
  // example, in cases where the input does not have focus.
  const eventListenerAdded = useRef<boolean>(false)

  const [internalValue, setInternalValue] = useState<string>(value)
  const [isExpanded, setIsExpanded] = useState<boolean>(!collapsible || !!value)
  const shouldFocusAfterExpand = useRef<boolean>(false)

  useEffect(() => {
    if (!isExpanded || !shouldFocusAfterExpand.current) return

    shouldFocusAfterExpand.current = false

    // Defer focus until after the wrapper's expand animation finishes;
    // focusing mid-animation scrolls the not-yet-fully-visible input into
    // view and bumps the layout.
    const timeoutId = window.setTimeout(() => {
      inputRef.current?.focus({ preventScroll: true })
    }, 300)

    return () => window.clearTimeout(timeoutId)
  }, [isExpanded])

  const onExpandedChangeRef = useRef(onExpandedChange)
  onExpandedChangeRef.current = onExpandedChange

  useEffect(() => {
    onExpandedChangeRef.current?.(isExpanded)
  }, [isExpanded])

  const cacheOnChange = useRef(onChange)
  cacheOnChange.current = onChange

  // Empty and close search if ESC key is clicked
  const onKeyUpHandler = useCallback((e: KeyboardEvent) => {
    if (e.key !== 'Escape') return

    e.preventDefault()

    setInternalValue('')
    cacheOnChange.current('')

    inputRef.current?.blur()
  }, [])

  useEffect(() => {
    setInternalValue(value)
  }, [value])

  useEffect(() => {
    if (internalValue && !eventListenerAdded.current) {
      document.addEventListener('keyup', onKeyUpHandler)
      eventListenerAdded.current = true
    }

    return () => {
      if (!internalValue && eventListenerAdded.current) {
        document.removeEventListener('keyup', onKeyUpHandler)
        eventListenerAdded.current = false
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [internalValue])

  const isCollapsed = !!collapsible && !isExpanded

  return (
    <Wrapper $expanded={!isCollapsed}>
      {children}

      <LoupeIconContainer
        type="button"
        aria-label={isCollapsed ? 'Search' : undefined}
        tabIndex={isCollapsed ? 0 : -1}
        disabled={disabled}
        $collapsed={isCollapsed}
        onClick={() => {
          if (isCollapsed) {
            shouldFocusAfterExpand.current = true
            setIsExpanded(true)
          } else {
            inputRef.current?.focus()
          }
        }}
      >
        <LoupeIcon data-testid="loupe-icon" />
      </LoupeIconContainer>

      {!isCollapsed && (
        <Form
          className={className}
          width={width}
          onSubmit={e => {
            e.preventDefault()
            onSubmit(e)
          }}
          {...rest}
        >
          <FormContent $fullWidth={fullWidth}>
            <SearchInput
              $hasContent={!!internalValue.length}
              ref={inputRef}
              name={name}
              type="search"
              value={internalValue}
              onKeyDown={onKeyDown}
              data-cwv-search-input={isCWV}
              placeholder={placeholder}
              disabled={disabled}
              onFocus={e => {
                onFocus(e)
              }}
              onBlur={e => {
                if (!internalValue) {
                  if (collapsible) {
                    setIsExpanded(false)
                  }
                  onBlur(e)
                }
              }}
              onChange={e => {
                setInternalValue(e.target.value)
                onChange(e.target.value)
              }}
            />
            <ClearButtonAndChildrenWrapper>
              {internalValue && (
                <ClearButton
                  onClick={() => {
                    inputRef.current?.focus()
                    setInternalValue('')
                    onChange('')
                    onSubmit()
                    onClear()
                  }}
                />
              )}
            </ClearButtonAndChildrenWrapper>
          </FormContent>
        </Form>
      )}
    </Wrapper>
  )
}

export const Filter = styled(FilterBase)``
